/** @jsx jsx */
import { useCallback } from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Inline, xcss } from '@atlaskit/primitives';
import { CellHoverProvider } from '@atlassian/jira-hover-popover/src/controllers/index.tsx';
import ShowMorePopup, { type Props, type MinimumItemData } from './show-more-popup/index.tsx';

const ListWithPopup = <TData extends MinimumItemData>({
	items,
	ItemComponent,
	maxLimit,
	initialIsOpen,
	isHoverPopoverEnabled,
	...restProps
}: Props<TData>) => {
	const [firstItem, ...restItems] = items;

	const ShowMorePopupRenderer = useCallback(
		() =>
			isHoverPopoverEnabled ? (
				<CellHoverProvider>
					<ShowMorePopup
						items={restItems}
						maxLimit={maxLimit}
						ItemComponent={ItemComponent}
						initialIsOpen={initialIsOpen}
						isHoverPopoverEnabled={isHoverPopoverEnabled}
						{...restProps}
					/>
				</CellHoverProvider>
			) : (
				<ShowMorePopup
					items={restItems}
					maxLimit={maxLimit}
					ItemComponent={ItemComponent}
					initialIsOpen={initialIsOpen}
					isHoverPopoverEnabled={isHoverPopoverEnabled}
					{...restProps}
				/>
			),
		[ItemComponent, initialIsOpen, isHoverPopoverEnabled, maxLimit, restItems, restProps],
	);

	if (items.length === 0) {
		return null;
	}

	return (
		<Inline
			space="space.100"
			alignBlock="center"
			shouldWrap={!isHoverPopoverEnabled}
			xcss={isHoverPopoverEnabled && hoverPopoverStyles}
		>
			<ItemComponent
				{...firstItem}
				isHoverPopoverEnabled={isHoverPopoverEnabled}
				css={isHoverPopoverEnabled && hoverPopoverItemStyles}
			/>

			{restItems.length >= 1 && ShowMorePopupRenderer()}
		</Inline>
	);
};

const hoverPopoverStyles = xcss({
	paddingRight: 'space.050',
	width: '100%',
});

const hoverPopoverItemStyles = css({
	minWidth: token('space.0'),
});

export default ListWithPopup;
